/**
 * Form08_PatientHealthHistoryQuestionnaire.jsx
 * Collection: patient_health_history
 * Redirect: /forms/09-patient-history-form
 */
import React, { useRef, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { FormShell, Field, SigBlock, CheckGroup, saveToMongo, downloadPDF, useToast, C } from "../shared/formKit";

const API_URL = "/api/forms/patient-health-history";
const NEXT    = "/forms/09-patient-history-form";

const IMMUNIZATIONS = ["chickenpox","fluShot","gardasilHPV","hepatitisA","hepatitisB",
  "meningococcus","MMR","pneumonia","tdap","tetanus","zostavax"];
const IMM_LABELS = {
  chickenpox:"Chickenpox", fluShot:"Flu Shot", gardasilHPV:"Gardasil/HPV",
  hepatitisA:"Hepatitis A", hepatitisB:"Hepatitis B", meningococcus:"Meningococcus",
  MMR:"MMR (Measles, Mumps, Rubella)", pneumonia:"Pneumonia", tdap:"Tdap (Tetanus & Pertussis)",
  tetanus:"Tetanus", zostavax:"Zostavax (Shingles)",
};

const PMH_LIST = [
  ["anxietyDisorder","Anxiety Disorder"],["arthritis","Arthritis"],["asthma","Asthma"],
  ["bleedingDisorder","Bleeding Disorder"],["bloodClots","Blood Clots (DVT)"],["cancer","Cancer"],
  ["coronaryArteryDisease","Coronary Artery Disease"],["claustrophobic","Claustrophobic"],
  ["diabetesInsulin","Diabetes - Insulin"],["diabetesNonInsulin","Diabetes - Non-Insulin"],
  ["dialysis","Dialysis"],["diverticulitis","Diverticulitis"],["fibromyalgia","Fibromyalgia"],
  ["gout","Gout"],["pacemaker","Has Pacemaker"],["heartAttack","Heart Attack"],
  ["heartMurmur","Heart Murmur"],["hiatalHernia","Hiatal Hernia or Reflux Disease"],
  ["hivAids","HIV or AIDS"],["highCholesterol","High Cholesterol"],["highBP","High Blood Pressure"],
  ["overactiveThyroid","Overactive Thyroid"],["kidneyDisease","Kidney Disease"],
  ["kidneyStones","Kidney Stones"],["legFootUlcers","Leg/Foot Ulcers"],
  ["liverDisease","Liver Disease"],["osteoporosis","Osteoporosis"],["polio","Polio"],
  ["pulmonaryEmbolism","Pulmonary Embolism"],["refluxUlcers","Reflux or Ulcers"],
  ["stroke","Stroke"],["tuberculosis","Tuberculosis"],["other","Other"],
];

const ROS_SYSTEMS = {
  allergicImmunologic: { label:"Allergic / Immunologic", items:[
    ["frequentSneezing","Frequent Sneezing"],["hives","Hives"],["itching","Itching"],
    ["runnyNose","Runny Nose"],["sinusPressure","Sinus Pressure"],
  ]},
  cardiovascular: { label:"Cardiovascular", items:[
    ["armPain","Arm Pain on Exertion"],["chestPain","Chest Pain on Exertion"],
    ["chestHeaviness","Chest Heaviness/Pressure on Exertion"],["palpitations","Irregular Heart Beats (Palpitations)"],
    ["heartMurmur","Known Heart Murmur"],["lightheaded","Light-headed on Standing"],
    ["sobLyingDown","Shortness of Breath When Lying Down"],["sobWalking","Shortness of Breath When Walking"],
    ["swellingEdema","Swelling (Edema)"],
  ]},
  constitutional: { label:"Constitutional", items:[
    ["exerciseIntolerance","Exercise Intolerance"],["fatigue","Fatigue"],["fever","Fever"],
    ["weightGain","Weight Gain"],["weightLoss","Weight Loss"],
  ]},
  eyes: { label:"Eyes", items:[
    ["dryEyes","Dry Eyes"],["irritation","Irritation"],["visionChange","Vision Change"],
  ]},
  earNoseMouthThroat: { label:"Ears / Nose / Mouth / Throat", items:[
    ["bleedingGums","Bleeding Gums"],["difficultyHearing","Difficulty Hearing"],
    ["dizzinessENMT","Dizziness"],["dryMouth","Dry Mouth"],["earPain","Ear Pain"],
    ["frequentInfections","Frequent Infections"],["nosebleeds","Frequent Nosebleeds"],
    ["hoarseness","Hoarseness"],["mouthBreathing","Mouth Breathing"],["mouthUlcers","Mouth Ulcers"],
    ["sinusProblems","Nose/Sinus Problems"],["ringingEars","Ringing in Ears"],
  ]},
  endocrine: { label:"Endocrine", items:[
    ["endocrineFatigue","Fatigue"],["increasedThirst","Increased Thirst/Hunger/Urination"],
  ]},
  gastrointestinal: { label:"Gastrointestinal", items:[
    ["abdominalPain","Abdominal Pain"],["blackStool","Black or Tarry Stool"],["bloodStool","Blood in Stool"],
    ["changeAppetite","Change in Appetite"],["indigestion","Frequent Indigestion"],
    ["hemorrhoids","Hemorrhoids"],["troubleSwallowing","Trouble Swallowing"],
    ["vomiting","Vomiting"],["vomitingBlood","Vomiting Blood"],
  ]},
  genitourinary: { label:"Genitourinary", items:[
    ["bloodUrine","Blood in Urine"],["difficultyUrinating","Difficulty Urinating"],
    ["incompleteEmptying","Incomplete Emptying"],["urinaryFrequency","Increased Urinary Frequency"],
    ["urinaryLossControl","Urinary Loss of Control"],
  ]},
  hematologicLymphatic: { label:"Hematologic / Lymphatic", items:[
    ["easyBruising","Easy Bruising/Bleeding"],["swollenGlands","Swollen Glands"],
  ]},
  integumentary: { label:"Integumentary (Skin)", items:[
    ["changesInMoles","Changes in Moles"],["drySkin","Dry Skin"],["eczema","Eczema"],
    ["growthLesions","Growth/Lesions"],["skinItching","Itching"],
    ["jaundice","Jaundice (Yellow Skin/Eyes)"],["rash","Rash"],
  ]},
  musculoskeletal: { label:"Musculoskeletal", items:[
    ["backPain","Back Pain"],["jointPain","Joint Pain"],["muscleAches","Muscle Aches"],["muscleWeakness","Muscle Weakness"],
  ]},
  neurological: { label:"Neurological", items:[
    ["dizzinessNeuro","Dizziness"],["fainting","Fainting"],["headaches","Headaches"],
    ["memoryLoss","Memory Loss"],["migraines","Migraines"],["numbness","Numbness"],
    ["restlessLegs","Restless Legs"],["seizures","Seizures"],["weakness","Weakness"],
  ]},
  psychiatric: { label:"Psychiatric", items:[
    ["alcoholOveruse","Alcohol Overuse"],["anxietyStress","Anxiety/Stress"],["depression","Depression"],
    ["unsafeRelationship","Do Not Feel Safe in Relationship"],["mania","Mania"],["sleepProblems","Sleep Problems"],
  ]},
  respiratory: { label:"Respiratory", items:[
    ["cough","Cough"],["coughingBlood","Coughing Up Blood"],["sob","Shortness of Breath"],
    ["sleepApnea","Sleep Apnea"],["snoring","Snoring"],["wheezing","Wheezing"],
  ]},
};

const initBool = (list) => Object.fromEntries(list.map(([k])=>[k,false]));
const initROS  = () => Object.fromEntries(Object.entries(ROS_SYSTEMS).map(([sys,{items}])=>[sys, initBool(items)]));

export default function PatientHealthHistoryQuestionnaire({
  patientName="", onSubmit,
}) {
  const history = useHistory();
  const formRef  = useRef(null);
  const sigRef   = useRef(null);
  const [sigEmpty, setSigEmpty]     = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savedId, setSavedId]       = useState(null);
  const [showToast, Toast]          = useToast();

  const [form, setForm] = useState({
    mainReason:       "",
    otherConcerns:    "",
    declineToComplete: false,

    allergies:        [{allergy:"",reaction:""},{allergy:"",reaction:""},{allergy:"",reaction:""}],
    favoritePharmacy: "",

    medications:      Array(10).fill(null).map(()=>({drugName:"",strength:"",frequency:""})),
    immunizations:    Object.fromEntries(IMMUNIZATIONS.map(k=>[k,{checked:false,date:""}])),

    // OB/GYN
    obgyn: {
      lastPapDate:"", papAbnormal:false,
      lastMammogramDate:"", mammogramAbnormal:false,
      ageFirstMenstrual:"", lastMenstrualOrMenopause:"",
      numPregnancies:"", numBirths:"", numMiscarriages:"", numAbortions:"",
      cesarean:false, numCesarean:"",
      symptoms: initBool([
        ["bleedingBetweenPeriods"],["heavyPeriods"],["extremeMenstrualPain"],
        ["vaginalDischarge"],["nightBathroom"],["hotFlashes"],
        ["breastLump"],["painfulIntercourse"],["sexuallyActive"],
      ]),
      sexPartner:"", usesCondoms:"", birthControlMethod:"", stiScreening:false,
    },

    pmh: initBool(PMH_LIST),
    surgeries: Array(4).fill(null).map(()=>({surgery:"",reason:"",year:"",hospital:""})),

    familyHistory: Object.fromEntries([
      "grandmotherMaternal","grandfatherMaternal","grandmotherPaternal","grandfatherPaternal",
      "father","mother","brotherSister","other",
    ].map(rel=>([rel,{
      alive:"",age:"",
      conditions: initBool([
        ["alcoholism"],["arthritis"],["depression"],["cancer"],["diabetes"],
        ["geneticDisease"],["heartDisease"],["hypertension"],["osteoporosis"],["stroke"],
      ]),
    }]))),

    social: {
      education:"", maritalStatus:"", exerciseLevel:"",
      caffeine:"", caffeinePerDay:"",
      drinksAlcohol:"", alcoholFrequency:"", drinksPerWeek:"",
      usesTobacco:"", everUsedTobacco:"",
      tobaccoType: initBool([["cigarettes"],["chew"],["cigars"]]),
      packsPerDay:"", chewPerDay:"", cigarsPerDay:"", yearsUsed:"", yearQuit:"",
      usesRecreationalDrugs:"", drugsList:"",
      occupation:"",
    },

    ros: initROS(),
    weightGainLbs:"", weightLossLbs:"",
    eyeLastExamDate:"",
    additionalNotes:"",

    responsibleParty: patientName,
    relationship:     "Self",
    sigDate:          new Date().toISOString().split("T")[0],
  });

  const set = useCallback((k,v) => setForm(f=>({...f,[k]:v})),[]);
  const setNested = useCallback((path, v) => {
    setForm(f => {
      const parts = path.split(".");
      const copy = {...f};
      let cur = copy;
      for (let i=0;i<parts.length-1;i++){
        cur[parts[i]] = {...cur[parts[i]]};
        cur = cur[parts[i]];
      }
      cur[parts[parts.length-1]] = v;
      return copy;
    });
  },[]);

  const setAllergyField = (i,field,val) => setForm(f=>{
    const a=[...f.allergies]; a[i]={...a[i],[field]:val}; return {...f,allergies:a};
  });
  const setMedField = (i,field,val) => setForm(f=>{
    const m=[...f.medications]; m[i]={...m[i],[field]:val}; return {...f,medications:m};
  });
  const setSurgeryField = (i,field,val) => setForm(f=>{
    const s=[...f.surgeries]; s[i]={...s[i],[field]:val}; return {...f,surgeries:s};
  });
  const setImmField = (k,field,val) => setForm(f=>({
    ...f, immunizations:{...f.immunizations,[k]:{...f.immunizations[k],[field]:val}}
  }));
  const setPmh = (k,v) => setForm(f=>({...f,pmh:{...f.pmh,[k]:v}}));
  const setRos = (sys,k,v) => setForm(f=>({
    ...f, ros:{...f.ros,[sys]:{...f.ros[sys],[k]:v}}
  }));
  const setFam = (rel,field,v) => setForm(f=>({
    ...f, familyHistory:{...f.familyHistory,[rel]:{...f.familyHistory[rel],[field]:v}}
  }));
  const setFamCond = (rel,k,v) => setForm(f=>({
    ...f, familyHistory:{...f.familyHistory,[rel]:{
      ...f.familyHistory[rel],
      conditions:{...f.familyHistory[rel].conditions,[k]:v}
    }}
  }));
  const setObgyn = (k,v) => setForm(f=>({...f,obgyn:{...f.obgyn,[k]:v}}));
  const setObgynSym = (k,v) => setForm(f=>({...f,obgyn:{...f.obgyn,symptoms:{...f.obgyn.symptoms,[k]:v}}}));
  const setSocial = (k,v) => setForm(f=>({...f,social:{...f.social,[k]:v}}));
  const setSocialTobacco = (k,v) => setForm(f=>({...f,social:{...f.social,tobaccoType:{...f.social.tobaccoType,[k]:v}}}));

  const getSig = () => (!sigRef.current||sigRef.current.isEmpty()) ? null : sigRef.current.toDataURL("image/png");

  const buildDoc = () => ({
    formType:        "patient_health_history",
    patient:         { name: patientName },
    mainReason:      form.mainReason,
    otherConcerns:   form.otherConcerns,
    declineToComplete: form.declineToComplete,
    allergies:       form.allergies.filter(a=>a.allergy),
    favoritePharmacy: form.favoritePharmacy,
    medications:     form.medications.filter(m=>m.drugName),
    immunizations:   form.immunizations,
    obgyn:           form.obgyn,
    pastMedicalHistory: form.pmh,
    surgeries:       form.surgeries.filter(s=>s.surgery),
    familyHistory:   form.familyHistory,
    socialHistory:   form.social,
    reviewOfSystems: form.ros,
    weightGainLbs:   form.weightGainLbs,
    weightLossLbs:   form.weightLossLbs,
    eyeLastExamDate: form.eyeLastExamDate,
    additionalNotes: form.additionalNotes,
    authorization: {
      responsibleParty: form.responsibleParty,
      relationship:     form.relationship,
      sigDate:          form.sigDate||null,
      signatureDataURL: getSig(),
    },
    meta: { submittedAt:new Date().toISOString(), formVersion:"1.0.0" },
  });

  const handleSubmit = async () => {
    if (sigEmpty) { showToast("Please sign before submitting.","error"); return; }
    setSubmitting(true);
    try {
      const doc = buildDoc();
      const res = typeof onSubmit==="function" ? await onSubmit(doc) : await saveToMongo(doc, API_URL);
      if (res?._id) setSavedId(res._id);
      showToast("Saved! Redirecting…","success");
      setTimeout(()=>history.push(NEXT),1800);
    } catch(e){ console.error(e); showToast("Save failed.","error"); }
    finally { setSubmitting(false); }
  };

  const handleDownload = async () => {
    try { await downloadPDF(formRef,`health-history-${patientName||"patient"}.pdf`); showToast("Downloaded!"); }
    catch { showToast("PDF failed.","error"); }
  };

  const SectionTitle = ({num,label})=>(
    <div className="wf-section-title"><span className="num">{num}</span>{label}</div>
  );

  return (
    <FormShell title="Patient Health History Questionnaire"
      subtitle="Your answers will help us better understand your medical concerns. All questions are optional and strictly confidential."
      dbLabel="MongoDB · patient_health_history"
      patientName={patientName} savedId={savedId} formRef={formRef}
      onSubmit={handleSubmit} onDownload={handleDownload} submitting={submitting}>

      {/* Decline */}
      <div className="wf-section">
        <label style={{display:"flex",gap:10,cursor:"pointer",alignItems:"center"}}>
          <input type="checkbox" style={{accentColor:C.teal}} checked={form.declineToComplete}
            onChange={e=>set("declineToComplete",e.target.checked)} />
          <span style={{fontSize:14,fontWeight:500}}>I willfully decline to complete this form.</span>
        </label>
      </div>

      {!form.declineToComplete && (<>

      {/* Visit reason */}
      <div className="wf-section">
        <SectionTitle num="1" label="Reason for Visit" />
        <div className="wf-grid" style={{gap:14}}>
          <Field label="Main reason for today's visit">
            <textarea value={form.mainReason} onChange={e=>set("mainReason",e.target.value)}
              placeholder="Describe your main concern…" style={{minHeight:60}} />
          </Field>
          <Field label="Other concerns">
            <textarea value={form.otherConcerns} onChange={e=>set("otherConcerns",e.target.value)}
              placeholder="Any other concerns to discuss…" style={{minHeight:60}} />
          </Field>
        </div>
      </div>

      {/* Allergies */}
      <div className="wf-section">
        <SectionTitle num="2" label="Allergies" />
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {form.allergies.map((a,i)=>(
            <div key={i} className="wf-grid wf-grid-2">
              <Field label={`Allergy ${i+1}`}>
                <input type="text" value={a.allergy} onChange={e=>setAllergyField(i,"allergy",e.target.value)}
                  placeholder="Medication, food, bee stings, etc." />
              </Field>
              <Field label="Reaction">
                <input type="text" value={a.reaction} onChange={e=>setAllergyField(i,"reaction",e.target.value)}
                  placeholder="Describe reaction" />
              </Field>
            </div>
          ))}
        </div>
      </div>

      {/* Pharmacy */}
      <div className="wf-section">
        <SectionTitle num="3" label="Favorite Pharmacy" />
        <Field label="Pharmacy Name &amp; Address">
          <input type="text" value={form.favoritePharmacy} onChange={e=>set("favoritePharmacy",e.target.value)}
            placeholder="Pharmacy name and address" />
        </Field>
      </div>

      {/* Medications */}
      <div className="wf-section">
        <SectionTitle num="4" label="Current Medications" />
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {form.medications.map((m,i)=>(
            <div key={i} className="wf-grid wf-grid-3">
              <Field label={`Drug Name ${i+1}`}>
                <input type="text" value={m.drugName} onChange={e=>setMedField(i,"drugName",e.target.value)}
                  placeholder="Drug name" />
              </Field>
              <Field label="Strength">
                <input type="text" value={m.strength} onChange={e=>setMedField(i,"strength",e.target.value)}
                  placeholder="e.g. 10mg" />
              </Field>
              <Field label="Frequency">
                <input type="text" value={m.frequency} onChange={e=>setMedField(i,"frequency",e.target.value)}
                  placeholder="e.g. Once daily" />
              </Field>
            </div>
          ))}
        </div>
      </div>

      {/* Immunizations */}
      <div className="wf-section">
        <SectionTitle num="5" label="Immunization History" />
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          {IMMUNIZATIONS.map(k=>(
            <div key={k} style={{display:"flex",alignItems:"center",gap:10,
              padding:"10px 12px",border:`1.5px solid ${C.border}`,borderRadius:8,
              background:form.immunizations[k].checked?C.tealLt:C.white}}>
              <input type="checkbox" style={{accentColor:C.teal}}
                checked={form.immunizations[k].checked}
                onChange={e=>setImmField(k,"checked",e.target.checked)} />
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:600}}>{IMM_LABELS[k]}</div>
                {form.immunizations[k].checked && (
                  <input type="date" value={form.immunizations[k].date}
                    onChange={e=>setImmField(k,"date",e.target.value)}
                    style={{marginTop:4,fontSize:12,padding:"4px 8px",border:`1px solid ${C.border}`,borderRadius:6,width:"100%"}} />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* OB/GYN */}
      <div className="wf-section">
        <SectionTitle num="6" label="Obstetric & Gynecological History (Women Only)" />
        <div className="wf-grid wf-grid-2" style={{marginBottom:16}}>
          <Field label="Last PAP Smear Date">
            <input type="date" value={form.obgyn.lastPapDate} onChange={e=>setObgyn("lastPapDate",e.target.value)} />
          </Field>
          <Field label="PAP Result">
            <label className="wf-check-label" style={{display:"inline-flex"}}>
              <input type="checkbox" checked={form.obgyn.papAbnormal} onChange={e=>setObgyn("papAbnormal",e.target.checked)} />
              Abnormal
            </label>
          </Field>
          <Field label="Last Mammogram Date">
            <input type="date" value={form.obgyn.lastMammogramDate} onChange={e=>setObgyn("lastMammogramDate",e.target.value)} />
          </Field>
          <Field label="Mammogram Result">
            <label className="wf-check-label" style={{display:"inline-flex"}}>
              <input type="checkbox" checked={form.obgyn.mammogramAbnormal} onChange={e=>setObgyn("mammogramAbnormal",e.target.checked)} />
              Abnormal
            </label>
          </Field>
          <Field label="Age of First Menstrual Period">
            <input type="number" min={0} max={30} value={form.obgyn.ageFirstMenstrual}
              onChange={e=>setObgyn("ageFirstMenstrual",e.target.value)} />
          </Field>
          <Field label="Last Menstrual Period / Age of Menopause">
            <input type="text" value={form.obgyn.lastMenstrualOrMenopause}
              onChange={e=>setObgyn("lastMenstrualOrMenopause",e.target.value)} placeholder="Date or age" />
          </Field>
          {[["numPregnancies","# Pregnancies"],["numBirths","Births"],
            ["numMiscarriages","Miscarriages"],["numAbortions","Abortions"]].map(([k,lbl])=>(
            <Field key={k} label={lbl}>
              <input type="number" min={0} value={form.obgyn[k]} onChange={e=>setObgyn(k,e.target.value)} />
            </Field>
          ))}
        </div>
        <Field label="Cesarean Sections?">
          <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
            <label className="wf-check-label">
              <input type="checkbox" checked={form.obgyn.cesarean} onChange={e=>setObgyn("cesarean",e.target.checked)} />
              Yes
            </label>
            {form.obgyn.cesarean && (
              <Field label="Number">
                <input type="number" min={1} value={form.obgyn.numCesarean}
                  onChange={e=>setObgyn("numCesarean",e.target.value)} style={{width:80}} />
              </Field>
            )}
          </div>
        </Field>
        <div style={{marginTop:14}}>
          <Field label="Symptoms (check all that apply)">
            <div className="wf-check-group">
              {[["bleedingBetweenPeriods","Bleeding between periods"],["heavyPeriods","Heavy periods"],
                ["extremeMenstrualPain","Extreme menstrual pain"],["vaginalDischarge","Vaginal itching/burning/discharge"],
                ["nightBathroom","Wake in the night to use bathroom"],["hotFlashes","Hot flashes"],
                ["breastLump","Breast lump or nipple discharge"],["painfulIntercourse","Painful intercourse"],
                ["sexuallyActive","Sexually active"]].map(([k,lbl])=>(
                <label className="wf-check-label" key={k}>
                  <input type="checkbox" checked={!!form.obgyn.symptoms[k]}
                    onChange={e=>setObgynSym(k,e.target.checked)} />{lbl}
                </label>
              ))}
            </div>
          </Field>
        </div>
        <div className="wf-grid wf-grid-3" style={{marginTop:14}}>
          <Field label="Current sexual partner">
            <div className="wf-radio-group">
              {["Female","Male","N/A"].map(o=>(
                <label className="wf-radio-label" key={o}>
                  <input type="radio" name="sexPartner" value={o}
                    checked={form.obgyn.sexPartner===o} onChange={e=>setObgyn("sexPartner",e.target.value)} />{o}
                </label>
              ))}
            </div>
          </Field>
          <Field label="Use condoms?">
            <div className="wf-radio-group">
              {["Yes","No"].map(o=>(
                <label className="wf-radio-label" key={o}>
                  <input type="radio" name="condoms" value={o}
                    checked={form.obgyn.usesCondoms===o} onChange={e=>setObgyn("usesCondoms",e.target.value)} />{o}
                </label>
              ))}
            </div>
          </Field>
          <Field label="STI Screening Interest">
            <label className="wf-check-label" style={{display:"inline-flex"}}>
              <input type="checkbox" checked={form.obgyn.stiScreening}
                onChange={e=>setObgyn("stiScreening",e.target.checked)} />Interested in STI screening
            </label>
          </Field>
        </div>
        <div style={{marginTop:14}}>
          <Field label="Other birth control method">
            <input type="text" value={form.obgyn.birthControlMethod}
              onChange={e=>setObgyn("birthControlMethod",e.target.value)} placeholder="Describe if applicable" />
          </Field>
        </div>
      </div>

      {/* Past Medical History */}
      <div className="wf-section">
        <SectionTitle num="7" label="Past Medical History" />
        <Field label="Check all that apply">
          <div className="wf-check-group">
            {PMH_LIST.map(([k,lbl])=>(
              <label className="wf-check-label" key={k}>
                <input type="checkbox" checked={!!form.pmh[k]} onChange={e=>setPmh(k,e.target.checked)} />{lbl}
              </label>
            ))}
          </div>
        </Field>
      </div>

      {/* Surgeries */}
      <div className="wf-section">
        <SectionTitle num="8" label="Past Surgical History" />
        {form.surgeries.map((s,i)=>(
          <div key={i} className="wf-grid wf-grid-4" style={{marginBottom:10}}>
            <Field label={`Surgery ${i+1}`}>
              <input type="text" value={s.surgery} onChange={e=>setSurgeryField(i,"surgery",e.target.value)} placeholder="Procedure" />
            </Field>
            <Field label="Reason">
              <input type="text" value={s.reason} onChange={e=>setSurgeryField(i,"reason",e.target.value)} placeholder="Reason" />
            </Field>
            <Field label="Year">
              <input type="number" value={s.year} onChange={e=>setSurgeryField(i,"year",e.target.value)} placeholder="Year" />
            </Field>
            <Field label="Hospital">
              <input type="text" value={s.hospital} onChange={e=>setSurgeryField(i,"hospital",e.target.value)} placeholder="Hospital name" />
            </Field>
          </div>
        ))}
      </div>

      {/* Family History */}
      <div className="wf-section">
        <SectionTitle num="9" label="Family Health History" />
        {Object.entries(form.familyHistory).map(([rel,data])=>{
          const relLabel = rel.replace(/([A-Z])/g," $1").replace(/^./,s=>s.toUpperCase());
          return (
            <div key={rel} style={{marginBottom:16,padding:"14px 16px",border:`1px solid ${C.border}`,borderRadius:10}}>
              <div style={{fontWeight:600,fontSize:14,marginBottom:10}}>{relLabel}</div>
              <div className="wf-grid wf-grid-3" style={{marginBottom:10}}>
                <Field label="Alive?">
                  <div className="wf-radio-group">
                    {["Y","N"].map(o=>(
                      <label className="wf-radio-label" key={o}>
                        <input type="radio" name={`${rel}_alive`} value={o}
                          checked={data.alive===o} onChange={e=>setFam(rel,"alive",e.target.value)} />{o==="Y"?"Yes":"No"}
                      </label>
                    ))}
                  </div>
                </Field>
                <Field label="Age">
                  <input type="number" value={data.age} onChange={e=>setFam(rel,"age",e.target.value)} placeholder="Age" />
                </Field>
              </div>
              <Field label="Significant Health Problems">
                <div className="wf-check-group">
                  {[["alcoholism","Alcoholism"],["arthritis","Arthritis"],["depression","Depression"],
                    ["cancer","Cancer"],["diabetes","Diabetes"],["geneticDisease","Genetic Disease"],
                    ["heartDisease","Heart Disease"],["hypertension","Hypertension"],
                    ["osteoporosis","Osteoporosis"],["stroke","Stroke"]].map(([k,lbl])=>(
                    <label className="wf-check-label" key={k}>
                      <input type="checkbox" checked={!!data.conditions[k]}
                        onChange={e=>setFamCond(rel,k,e.target.checked)} />{lbl}
                    </label>
                  ))}
                </div>
              </Field>
            </div>
          );
        })}
      </div>

      {/* Social History */}
      <div className="wf-section">
        <SectionTitle num="10" label="Social History" />
        <div className="wf-grid wf-grid-3" style={{marginBottom:16}}>
          <Field label="Occupation">
            <input type="text" value={form.social.occupation} onChange={e=>setSocial("occupation",e.target.value)} />
          </Field>
          <Field label="Education">
            <div className="wf-radio-group">
              {["Less than 8th grade","High school","2 year college","4 year college","Post graduate"].map(o=>(
                <label className="wf-radio-label" key={o}>
                  <input type="radio" name="education" value={o}
                    checked={form.social.education===o} onChange={e=>setSocial("education",e.target.value)} />{o}
                </label>
              ))}
            </div>
          </Field>
          <Field label="Marital Status">
            <div className="wf-radio-group">
              {["Married","Single","Divorced","Separated","Widowed","Domestic partner"].map(o=>(
                <label className="wf-radio-label" key={o}>
                  <input type="radio" name="marital" value={o}
                    checked={form.social.maritalStatus===o} onChange={e=>setSocial("maritalStatus",e.target.value)} />{o}
                </label>
              ))}
            </div>
          </Field>
        </div>
        <div className="wf-grid wf-grid-2" style={{marginBottom:16}}>
          <Field label="Exercise Level">
            <div className="wf-radio-group">
              {["None","Occasional","Moderate","High level"].map(o=>(
                <label className="wf-radio-label" key={o}>
                  <input type="radio" name="exercise" value={o}
                    checked={form.social.exerciseLevel===o} onChange={e=>setSocial("exerciseLevel",e.target.value)} />{o}
                </label>
              ))}
            </div>
          </Field>
          <Field label="Caffeine Intake">
            <div className="wf-radio-group" style={{marginBottom:8}}>
              {["None","Occasional","Moderate","Heavy"].map(o=>(
                <label className="wf-radio-label" key={o}>
                  <input type="radio" name="caffeine" value={o}
                    checked={form.social.caffeine===o} onChange={e=>setSocial("caffeine",e.target.value)} />{o}
                </label>
              ))}
            </div>
            {form.social.caffeine && form.social.caffeine!=="None" && (
              <input type="number" value={form.social.caffeinePerDay}
                onChange={e=>setSocial("caffeinePerDay",e.target.value)} placeholder="# cups/cans per day" />
            )}
          </Field>
        </div>
        <div className="wf-grid wf-grid-2" style={{marginBottom:16}}>
          <Field label="Drinks Alcohol?">
            <div className="wf-radio-group" style={{marginBottom:8}}>
              {["Yes","No"].map(o=>(
                <label className="wf-radio-label" key={o}>
                  <input type="radio" name="alcohol" value={o}
                    checked={form.social.drinksAlcohol===o} onChange={e=>setSocial("drinksAlcohol",e.target.value)} />{o}
                </label>
              ))}
            </div>
            {form.social.drinksAlcohol==="Yes" && (<>
              <div className="wf-radio-group" style={{marginBottom:8}}>
                {["Occasionally","< 3 times/week","> 3 times/week"].map(o=>(
                  <label className="wf-radio-label" key={o}>
                    <input type="radio" name="alcoholFreq" value={o}
                      checked={form.social.alcoholFrequency===o} onChange={e=>setSocial("alcoholFrequency",e.target.value)} />{o}
                  </label>
                ))}
              </div>
              <input type="number" value={form.social.drinksPerWeek}
                onChange={e=>setSocial("drinksPerWeek",e.target.value)} placeholder="Drinks per week" />
            </>)}
          </Field>
          <Field label="Uses Tobacco?">
            <div className="wf-radio-group" style={{marginBottom:8}}>
              {["Yes","No"].map(o=>(
                <label className="wf-radio-label" key={o}>
                  <input type="radio" name="tobacco" value={o}
                    checked={form.social.usesTobacco===o} onChange={e=>setSocial("usesTobacco",e.target.value)} />{o}
                </label>
              ))}
            </div>
            {form.social.usesTobacco==="No" && (
              <div>
                <div style={{fontSize:12,fontWeight:600,color:C.muted,marginBottom:6}}>Ever used tobacco?</div>
                <div className="wf-radio-group">
                  {["Yes","No"].map(o=>(
                    <label className="wf-radio-label" key={o}>
                      <input type="radio" name="everTobacco" value={o}
                        checked={form.social.everUsedTobacco===o} onChange={e=>setSocial("everUsedTobacco",e.target.value)} />{o}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {(form.social.usesTobacco==="Yes"||(form.social.usesTobacco==="No"&&form.social.everUsedTobacco==="Yes")) && (
              <div style={{marginTop:8}}>
                <div className="wf-check-group" style={{marginBottom:8}}>
                  {[["cigarettes","Cigarettes"],["chew","Chew"],["cigars","Cigars"]].map(([k,lbl])=>(
                    <label className="wf-check-label" key={k}>
                      <input type="checkbox" checked={!!form.social.tobaccoType[k]}
                        onChange={e=>setSocialTobacco(k,e.target.checked)} />{lbl}
                    </label>
                  ))}
                </div>
                <div className="wf-grid wf-grid-2">
                  {[["packsPerDay","Packs/day"],["yearsUsed","# years"],["yearQuit","Year quit"]].map(([k,lbl])=>(
                    <Field key={k} label={lbl}>
                      <input type="text" value={form.social[k]} onChange={e=>setSocial(k,e.target.value)} />
                    </Field>
                  ))}
                </div>
              </div>
            )}
          </Field>
        </div>
        <Field label="Recreational / Street Drugs?">
          <div className="wf-radio-group" style={{marginBottom:8}}>
            {["Yes","No"].map(o=>(
              <label className="wf-radio-label" key={o}>
                <input type="radio" name="drugs" value={o}
                  checked={form.social.usesRecreationalDrugs===o} onChange={e=>setSocial("usesRecreationalDrugs",e.target.value)} />{o}
              </label>
            ))}
          </div>
          {form.social.usesRecreationalDrugs==="Yes" && (
            <textarea value={form.social.drugsList} onChange={e=>setSocial("drugsList",e.target.value)}
              placeholder="Please list…" style={{minHeight:60}} />
          )}
        </Field>
      </div>

      {/* Review of Systems */}
      <div className="wf-section">
        <SectionTitle num="11" label="Review of Systems" />
        {Object.entries(ROS_SYSTEMS).map(([sys,{label,items}])=>(
          <div key={sys} style={{marginBottom:16}}>
            <div style={{fontSize:13,fontWeight:700,color:C.navy,marginBottom:8,textTransform:"uppercase",letterSpacing:"0.5px"}}>{label}</div>
            <div className="wf-check-group">
              {items.map(([k,lbl])=>(
                <label className="wf-check-label" key={k}>
                  <input type="checkbox" checked={!!form.ros[sys][k]}
                    onChange={e=>setRos(sys,k,e.target.checked)} />{lbl}
                </label>
              ))}
            </div>
          </div>
        ))}
        <div className="wf-grid wf-grid-3" style={{marginTop:10}}>
          <Field label="Weight Gain (lbs)"><input type="number" value={form.weightGainLbs} onChange={e=>set("weightGainLbs",e.target.value)} /></Field>
          <Field label="Weight Loss (lbs)"><input type="number" value={form.weightLossLbs} onChange={e=>set("weightLossLbs",e.target.value)} /></Field>
          <Field label="Last Eye Exam Date"><input type="date" value={form.eyeLastExamDate} onChange={e=>set("eyeLastExamDate",e.target.value)} /></Field>
        </div>
      </div>

      {/* Additional notes */}
      <div className="wf-section">
        <SectionTitle num="12" label="Additional Information" />
        <Field label="Any other health information your provider should know">
          <textarea value={form.additionalNotes} onChange={e=>set("additionalNotes",e.target.value)}
            placeholder="Add any notes here…" style={{minHeight:80}} />
        </Field>
      </div>

      </>)}

      <SigBlock sigRef={sigRef} sigEmpty={sigEmpty} setSigEmpty={setSigEmpty}
        responsibleParty={form.responsibleParty} setResponsibleParty={v=>set("responsibleParty",v)}
        relationship={form.relationship} setRelationship={v=>set("relationship",v)}
        sigDate={form.sigDate} setSigDate={v=>set("sigDate",v)} />

      {Toast}
    </FormShell>
  );
}

/*
── Mongoose Schema (abbreviated — document is highly nested) ──────────────────
const PatientHealthHistorySchema = new Schema({
  formType: { type:String, default:"patient_health_history" },
  patient:  { name:String },
  mainReason:String, otherConcerns:String, declineToComplete:Boolean,
  allergies:        [{ allergy:String, reaction:String }],
  favoritePharmacy: String,
  medications:      [{ drugName:String, strength:String, frequency:String }],
  immunizations:    { type:Map, of: { checked:Boolean, date:String } },
  obgyn:            Schema.Types.Mixed,
  pastMedicalHistory: Schema.Types.Mixed,
  surgeries:        [{ surgery:String, reason:String, year:Number, hospital:String }],
  familyHistory:    Schema.Types.Mixed,
  socialHistory:    Schema.Types.Mixed,
  reviewOfSystems:  Schema.Types.Mixed,
  weightGainLbs:String, weightLossLbs:String, eyeLastExamDate:String,
  additionalNotes:String,
  authorization: { responsibleParty:String, relationship:String, sigDate:Date, signatureDataURL:String },
  meta: { submittedAt:Date, formVersion:String },
}, { timestamps:true, collection:"patient_health_history" });
*/
